import './ShipmentForm.scss';

import { Alert, Form, Modal } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FormRecordHeader from '@/components/general/FormRecordHeader';
import { FormSection } from '@/components/general/FormSection';
// Mejora: se reemplazó ShipmentAddressForm por el componente genérico LogisticAddressFormSection
import LogisticAddressFormSection from '@/components/logistic/LogisticAddressFormSection';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { COUNTRY, useCountryContext } from '@/contexts/CountryContext';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
// Mejora: se reemplazó el Modal.error manual y onFinishFailed inline por helpers genéricos
// (showContactWarningModal, createFinishFailedHandler) para eliminar duplicidad con ReceptionForm.
// Se reemplazó el header manual de edición por FormRecordHeader y las Cards repetidas por FormSection.
import { createFinishFailedHandler, showContactWarningModal } from '@/utils/formHelpers';

import ShipmentDetailForm from './ShipmentDetailForm';
import ShipmentPaymentForm from './ShipmentPaymentForm';
import { useShipmentForm } from './useShipmentForm';

interface ShipmentFormProps {
  formProps: FormProps;
  edit?: boolean;
  saveAddress: boolean;
  setSaveAddress: (value: boolean) => void;
  packageItems: PackageItem[];
  setPackageItems: (items: PackageItem[]) => void;
}

const ShipmentForm = (props: ShipmentFormProps) => {
  const { isBrasilSelected } = useCountryContext();
  const { t } = useTranslation();
  const { formProps, edit, setSaveAddress, packageItems, setPackageItems } = props;
  const [sp] = useSearchParams();
  const ticketId = sp.get('ticket') ? Number(sp.get('ticket')) : undefined;
  const { initialValues } = formProps;
  const initialValuesModified = useMemo(
    () =>
      initialValues
        ? {
            ...initialValues,
            company: initialValues?.company?.id,
            shipment_payment_company: initialValues?.shipment_payment_company?.id,
            shipper: initialValues?.shipper?.id,
            receiver_localidad: initialValues?.receiver_localidad?.id,
            receiver_provincia: initialValues?.receiver_localidad?.provincia?.id,
            receiver_pais: initialValues?.receiver_localidad?.provincia?.pais?.id,
            ticket: edit ? initialValues?.ticket : ticketId ? ticketId : initialValues?.ticket?.id,
          }
        : ticketId
          ? {
              ticket: ticketId,
              receiver_pais: isBrasilSelected ? COUNTRY.BRASIL : COUNTRY.ARGENTINA,
            }
          : { receiver_pais: isBrasilSelected ? COUNTRY.BRASIL : COUNTRY.ARGENTINA },

    [edit, initialValues, isBrasilSelected, ticketId],
  );
  const {
    companySelectProps,
    shippersSelectProps,
    ticketsSelectProps,
    technicianSelectProps,
    initialValuesSetted,
    blockPreventive,
    movilLink,
  } = useShipmentForm(formProps, initialValuesModified, edit);

  return (
    <Form
      {...formProps}
      onFinish={values => {
        if (
          blockPreventive &&
          packageItems.some(
            item =>
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.PREVENTIVO ||
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE ||
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION,
          )
        ) {
          Modal.error({
            title: t('Envío preventivo bloqueado'),
            content: t(
              'El envío contiene elementos preventivos, sujetos a peritaje o sin devolución y la empresa de facturación o la empresa destino tienen un bloqueo activo para este tipo de envíos',
            ),
            okText: t('Aceptar'),
            maskClosable: true,
          });

          return;
        }

        const companyWarn = formProps.form?.getFieldWarning('company') || [];
        const shipmentPaymentComapnyWarn =
          formProps.form?.getFieldWarning('shipment_payment_company') || [];
        const warnings: { companyId: number | string; label: string }[] = [];

        if (companyWarn?.length > 0) {
          warnings.push({
            companyId: values.company,
            label: t('Empresa o Técnico que recibirá el paquete'),
          });
        }

        if (shipmentPaymentComapnyWarn?.length > 0) {
          warnings.push({
            companyId: values.shipment_payment_company,
            label: t('Empresa para facturación'),
          });
        }

        // Mejora: showContactWarningModal centraliza el Modal.error de contacto faltante
        // que antes estaba duplicado manualmente en ShipmentForm y ReceptionForm
        if (showContactWarningModal(warnings, t)) {
          return;
        }

        if (formProps?.onFinish) {
          if (values.technician === -1) {
            values.technician = null;
          }

          formProps.onFinish(values);
        }
      }}
      layout="vertical"
      className="ShipmentForm"
      initialValues={initialValuesModified}
      // Mejora: handler genérico que reemplaza el bloque onFinishFailed duplicado en 4 formularios
      onFinishFailed={createFinishFailedHandler(t)}>
      {/* Mejora: FormRecordHeader reemplaza el bloque manual de ID + estado + fechas */}
      {edit && (
        <FormRecordHeader
          id={initialValues?.id}
          label={t('Id de envío')}
          statusTag={
            initialValues?.status != undefined ? (
              <PackageStateTag packageStatus={initialValues?.status} />
            ) : undefined
          }
          createdAt={initialValues?.created_at}
          updatedAt={initialValues?.updated_at}
          className="FormRecordHeader"
        />
      )}
      {/* Mejora: FormSection reemplaza el patrón repetido <Card><div className="title">...</div>...</Card> */}
      <FormSection className="ShipmentFormSection">
        <Form.Item
          label={<b>{t('Ticket')}</b>}
          name={'ticket'}
          rules={[
            {
              required: true,
            },
          ]}>
          <AntdSelect {...ticketsSelectProps} allowClear showSearch />
        </Form.Item>
      </FormSection>
      {blockPreventive && (
        <Alert
          style={{ padding: 16 }}
          type="error"
          showIcon
          message={
            <b>
              {t(
                'Este cliente esta sujeto a un bloqueo de envíos preventivos, sujetos a peritaje o sin devolución',
              )}
            </b>
          }
        />
      )}
      <FormSection title={t('Detalles de facturación')} className="ShipmentFormSection">
        <ShipmentPaymentForm
          formProps={formProps}
          companySelectProps={companySelectProps}
          edit={edit}
        />
      </FormSection>
      <FormSection title={t('Detalles del pedido')} className="ShipmentFormSection">
        <ShipmentDetailForm
          formProps={formProps}
          companySelectProps={companySelectProps}
          shippersSelectProps={shippersSelectProps}
          ticketsSelectProps={ticketsSelectProps}
          edit={edit}
        />
      </FormSection>

      <FormSection title={t('Dirección de envío')} className="ShipmentFormSection">
        <LogisticAddressFormSection
          formProps={formProps}
          initialValuesModified={initialValuesModified}
          setSaveAddress={setSaveAddress}
          initialValuesSetted={initialValuesSetted}
          rowClassName="ShipmentFormRow"
        />
      </FormSection>
      <FormSection className="ShipmentFormSection">
        <Form.Item
          label={<b>{t('Técnico involucrado')}</b>}
          name={'technician'}
          rules={[{ required: true, message: t('Campo requerido') }]}>
          <AntdSelect
            {...technicianSelectProps}
            options={[
              { label: 'No habrá técnico involucrado', value: -1 },
              ...(technicianSelectProps.options ?? []),
            ]}
            allowClear
            showSearch
          />
        </Form.Item>
      </FormSection>
      <div className="ShipmentFormTitle">{t('Elementos')}</div>
      <PackageItemsList
        packageItems={packageItems}
        setPackageItems={setPackageItems}
        package={{ id: initialValues?.id, movil_link: movilLink }}
        editMode="enabled"
      />
    </Form>
  );
};

export default ShipmentForm;
